import React, { useState, useMemo, useEffect } from 'react';


/* ==============================================================================================
   1. DESIGN SYSTEM & ASSETS
   ============================================================================================== */


const LYWO_CSS = `
:root {
  --lywo-primary: #0d6efd;
  --lywo-bg: #f5f8ff;
  --lywo-text: #212529;
  --lywo-text-muted: #64748b;
  --lywo-border: #e2e8f0;
  --lywo-radius: 0.75rem;
  --lywo-success: #198754; --lywo-success-bg: #d1e7dd;
  --lywo-warning: #856404; --lywo-warning-bg: #fff3cd;
  --lywo-danger: #dc3545;
}


body { font-family: 'Inter', sans-serif; background: #f0f2f5; color: var(--lywo-text); overflow: hidden; margin: 0; }


/* Layouts */
.app-container { height: 100vh; display: flex; flex-direction: column; background: var(--lywo-bg); }
.app-header { background: #fff; padding: 0.75rem 1.5rem; border-bottom: 1px solid var(--lywo-border); display: flex; align-items: center; justify-content: space-between; flex-shrink: 0; }
.app-content { flex: 1; overflow: hidden; display: flex; flex-direction: column; padding: 1.5rem; width: 100%; max-width: 100%; box-sizing: border-box; }


/* Panels */
.panel { background: #fff; border-radius: var(--lywo-radius); box-shadow: 0 2px 4px rgba(0,0,0,0.02); display: flex; flex-direction: column; height: 100%; width: 100%; border: 1px solid var(--lywo-border); overflow: hidden; }
.panel-header { padding: 1rem 1.5rem; border-bottom: 1px solid var(--lywo-border); background: #fff; flex-shrink: 0; display: flex; justify-content: space-between; align-items: center; }
.panel-body { padding: 1.5rem; overflow-y: auto; flex: 1; width: 100%; }
.panel-footer { padding: 1rem 1.5rem; border-top: 1px solid var(--lywo-border); background: #fff; flex-shrink: 0; display: flex; justify-content: space-between; align-items: center; }


/* Components */
.btn { font-weight: 600; padding: 0.5rem 1rem; border-radius: 0.5rem; display: inline-flex; align-items: center; gap: 0.5rem; cursor: pointer; transition: all 0.2s; border: 1px solid transparent; font-size: 0.9rem; }
.btn-primary { background: var(--lywo-primary); color: #fff; border-color: var(--lywo-primary); }
.btn-primary:hover { opacity: 0.9; transform: translateY(-1px); }
.btn-outline { background: #fff; color: var(--lywo-text-muted); border-color: var(--lywo-border); }
.btn-outline:hover { border-color: var(--lywo-primary); color: var(--lywo-primary); }
.btn-danger-ghost { color: var(--lywo-danger); background: transparent; }
.btn-danger-ghost:hover { background: #fee2e2; }


.card-item { background: #fff; border: 1px solid var(--lywo-border); border-radius: 0.5rem; padding: 1rem; transition: transform 0.2s; height: 100%; display: flex; flex-direction: column; width: 100%; }
.card-item:hover { border-color: var(--lywo-primary); transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.05); }


/* Sticky Action Bar (Footer) */
.sticky-action-bar {
  position: sticky; bottom: 0; left: 0; right: 0;
  background: white; padding: 12px 24px;
  border-top: 1px solid var(--lywo-border);
  display: flex; justify-content: space-between; align-items: center;
  margin: 0 -1.5rem -1.5rem -1.5rem; /* Negative margins to flush with panel padding */
  z-index: 10;
}


.badge { padding: 0.35em 0.65em; border-radius: 6px; font-size: 0.75em; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
.badge-primary { background: #e0e7ff; color: var(--lywo-primary); }
.badge-success { background: var(--lywo-success-bg); color: var(--lywo-success); }
.badge-warning { background: var(--lywo-warning-bg); color: var(--lywo-warning); }


.tab-nav { display: flex; gap: 1.5rem; border-bottom: 1px solid var(--lywo-border); margin-bottom: 1rem; }
.tab-link { padding: 0.75rem 0; background: none; border: none; color: var(--lywo-text-muted); font-weight: 600; cursor: pointer; border-bottom: 2px solid transparent; }
.tab-link.active { color: var(--lywo-primary); border-bottom-color: var(--lywo-primary); }


.form-control { width: 100%; padding: 0.6rem; border: 1px solid var(--lywo-border); border-radius: 0.5rem; font-size: 0.9rem; }
.form-control:focus { outline: none; border-color: var(--lywo-primary); box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.1); }
.btn-icon {
  background: transparent;
  border: none;
  outline: none;
  padding: 0;
  cursor: pointer;
  box-shadow: none;
}

.btn-icon:focus,
.btn-icon:active {
  outline: none;
  box-shadow: none;
}

/* Visual Helpers */
.rationale-box {
    background-color: var(--lywo-warning-bg);
    color: var(--lywo-warning);
    border: 1px solid #ffeeba;
    padding: 0.75rem;
    border-radius: 0.5rem;
    font-size: 0.85rem;
    margin-top: 0.5rem;
}


/* Table Utilities for No-Wrap */
.table-nowrap th, .table-nowrap td { white-space: nowrap; }
.text-truncate-cell { max-width: 250px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block; }


.anim-fade { animation: fadeIn 0.3s ease-out; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
`;


const Icons = {
  Edit: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  Trash: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  Play: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3"/></svg>,
  Chart: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  Sparkles: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L12 3Z"/><path d="M5 3v4"/><path d="M9 5H3"/></svg>,
  Grid: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
  List: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>,
  Upload: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
  Check: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  Refresh: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>,
  More: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="1"/><circle cx="12" cy="5" r="1"/><circle cx="12" cy="19" r="1"/></svg>,
  Close: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  Download: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
  File: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
};


/* ==============================================================================================
   2. MOCK SERVICES
   ============================================================================================== */


const MockService = {
  simulateAi: (type, payload) => new Promise(resolve => {
    setTimeout(() => {
      const qTemplate = (i, topic) => ({
        id: Math.random().toString(36),
        text: `AI Question ${i + 1} regarding ${topic}`,
        type: 'MCQ',
        weight: 1.0,
        rationale: "AI generated rationale explaining the correct answer.",
        options: [{id:'a', text:'Option A'}, {id:'b', text:'Option B'}, {id:'c', text:'Option C'}, {id:'d', text:'Option D'}],
        correctOptionId: 'a',
        topics: ['AI', topic]
      });


      if (type === 'GET') resolve({ questions: Array(parseInt(payload.count)).fill(0).map((_, i) => qTemplate(i, payload.topic)) });
      if (type === 'REPLACE') resolve({ question: qTemplate(99, 'Replacement') });
      if (type === 'REGENERATE') resolve({ question: { ...qTemplate(99, 'Regen'), text: `Regenerated: ${payload.feedback}` } });
    }, 800);
  })
};


/* ==============================================================================================
   3. SHARED COMPONENTS
   ============================================================================================== */


const Modal = ({ title, onClose, children, footer }) => (
  <div style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1100, display:'flex', alignItems:'center', justifyContent:'center'}}>
    <div className="panel anim-fade" style={{width:'100%', maxWidth:'600px', maxHeight:'90vh', height:'auto'}}>
      <div className="panel-header">
        <h3 style={{margin:0, fontSize:'1.1rem', fontWeight:700}}>{title}</h3>
        <button onClick={onClose} style={{background:'none', border:'none', cursor:'pointer'}} title="Close"><Icons.Close/></button>
      </div>
      <div className="panel-body">{children}</div>
      {footer && <div className="panel-footer">{footer}</div>}
    </div>
  </div>
);


const Toast = ({ msg }) => (
  <div className="anim-fade" style={{position:'fixed', bottom:20, right:20, background:'#212529', color:'#fff', padding:'10px 20px', borderRadius:'8px', zIndex:1200, fontSize:'0.9rem', boxShadow:'0 4px 12px rgba(0,0,0,0.15)'}}>
    {msg}
  </div>
);


const OriginBadge = ({ origin }) => {
  const map = { 'AI-Generated': {c:'badge-primary', i:Icons.Sparkles}, 'Bulk Upload': {c:'badge-success', i:Icons.Upload}, 'Manual': {c:'badge-warning', i:Icons.Edit} };
  const {c, i:I} = map[origin] || map['Manual'];
  return <span className={`badge ${c}`} style={{display:'inline-flex', alignItems:'center', gap:'4px'}} title={`Origin: ${origin}`}><I style={{fontSize:'0.9em'}}/> {origin}</span>;
};


/* ==============================================================================================
   4. FEATURE VIEWS
   ============================================================================================== */


// --- Dashboard (Drafts) ---
const DashboardView = ({ assessments, repoType, setRepoType, companyName, onEdit, onDelete }) => {
  const [viewMode, setViewMode] = useState('grid');
  const data = assessments.filter(a => (a.status === 'Draft' || a.status === 'Master') && (a.repository || 'lywo') === repoType);


  return (
    <div className="panel">
      <div className="panel-header">
        <div className="tab-nav" style={{marginBottom:0, border:0}}>
          <button onClick={() => setRepoType('lywo')} className={`tab-link ${repoType === 'lywo' ? 'active' : ''}`} title="View LYWO Master Repository">LYWO Master</button>
          <button onClick={() => setRepoType('company')} className={`tab-link ${repoType === 'company' ? 'active' : ''}`} title={`View ${companyName} Repository`}>{companyName} Repository</button>
        </div>
        <div style={{display:'flex', gap:'8px'}}>
          <button className={`btn ${viewMode==='grid'?'btn-primary':'btn-outline'}`} onClick={()=>setViewMode('grid')} title="Grid View"><Icons.Grid/></button>
          <button className={`btn ${viewMode==='list'?'btn-primary':'btn-outline'}`} onClick={()=>setViewMode('list')} title="List View"><Icons.List/></button>
        </div>
      </div>
      <div className="panel-body" style={{background: '#f8f9fa'}}>
        {data.length === 0 ? <div style={{textAlign:'center', padding:'4rem', color:'#aaa'}}>No assessments found.</div> :
         viewMode === 'grid' ? (
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:'1rem'}}>
            {data.map(a => (
              <div key={a.id} className="card-item">
                <div style={{display:'flex', justifyContent:'space-between', marginBottom:'0.5rem'}}>
                  <OriginBadge origin={a.origin}/>
                  <div style={{display:'flex', gap:'0.25rem'}}>
                    <button className="btn-icon" onClick={()=>onEdit(a)} title="Edit Assessment"><Icons.Edit style={{color:'var(--lywo-primary)'}}/></button>
                    <button className="btn-icon" onClick={()=>onDelete(a.id)} title="Delete Assessment"><Icons.Trash style={{color:'var(--lywo-danger)'}}/></button>
                  </div>
                </div>
                <h4 style={{margin:'0 0 0.5rem 0', fontSize:'1rem', fontWeight:700}} title={a.title}>{a.title}</h4>
                <div style={{fontSize:'0.85rem', color:'var(--lywo-text-muted)', marginTop:'auto', borderTop:'1px solid var(--lywo-border)', paddingTop:'0.5rem'}}>
                  {a.questions.length} Questions • {a.durationMinutes} Minutes<br/>
                  <small title={`Updated on ${new Date(a.updatedAt).toLocaleDateString()}`}>Updated {new Date(a.updatedAt).toLocaleDateString()} by {a.updatedBy}</small>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <table style={{width:'100%', borderCollapse:'collapse', fontSize:'0.9rem', tableLayout: 'fixed'}}>
            <thead><tr style={{borderBottom:'2px solid var(--lywo-border)', textAlign:'left'}}><th style={{padding:'0.75rem', width:'90px'}}>Status</th><th style={{padding:'0.75rem'}}>Title</th><th style={{padding:'0.75rem', width:'130px'}}>Origin</th><th style={{padding:'0.75rem', width:'140px'}}>Updated</th><th style={{textAlign:'right', padding:'0.75rem', width:'100px'}}>Actions</th></tr></thead>
            <tbody>
              {data.map(a => (
                <tr key={a.id} style={{borderBottom:'1px solid var(--lywo-border)'}}>
                  <td style={{padding:'0.75rem', whiteSpace: 'nowrap'}}><span className="badge badge-success" title="Ready for assignment">{a.status}</span></td>
                  <td style={{padding:'0.75rem'}}>
                      <div className="text-truncate-cell" style={{fontWeight: 600}} title={a.title}>{a.title}</div>
                      <small style={{color:'#666', whiteSpace: 'nowrap'}}>{a.questions.length} Qs • {a.durationMinutes} m</small>
                  </td>
                  <td style={{padding:'0.75rem'}}><OriginBadge origin={a.origin}/></td>
                  <td style={{padding:'0.75rem', whiteSpace: 'nowrap'}} title={`Last updated by ${a.updatedBy}`}>{new Date(a.updatedAt).toLocaleDateString()}<br/><small>{a.updatedBy}</small></td>
                  <td style={{textAlign:'right', padding:'0.75rem', whiteSpace: 'nowrap'}}>
                     <button onClick={()=>onEdit(a)} title="Edit Assessment" style={{marginRight:'8px', background:'none', border:'none', cursor:'pointer', color:'var(--lywo-primary)'}}><Icons.Edit/></button>
                     <button onClick={()=>onDelete(a.id)} title="Delete Assessment" style={{background:'none', border:'none', cursor:'pointer', color:'var(--lywo-danger)'}}><Icons.Trash/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};


// --- Deployed (Active) ---
const DeployedView = ({ assessments, submissions, onAction, onDelete }) => {
  const [viewMode, setViewMode] = useState('grid');
  const active = assessments.filter(a => a.status === 'In Use');


  return (
    <div className="panel">
      <div className="panel-header">
        <h3 style={{margin:0, fontSize:'1.1rem'}}>Deployed Quiz</h3>
        <div style={{display:'flex', gap:'8px'}}>
          <button className={`btn ${viewMode==='grid'?'btn-primary':'btn-outline'}`} onClick={()=>setViewMode('grid')} title="Grid View"><Icons.Grid/></button>
          <button className={`btn ${viewMode==='list'?'btn-primary':'btn-outline'}`} onClick={()=>setViewMode('list')} title="List View"><Icons.List/></button>
        </div>
      </div>
      <div className="panel-body" style={{background: '#f8f9fa'}}>
        {viewMode === 'grid' ? (
          <div style={{display:'flex', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:'1rem'}}>
            {active.map(a => (
              <div key={a.id} className="card-item" style={{borderLeft:'4px solid var(--lywo-primary)'}}>
                <div style={{display:'flex', justifyContent:'space-between', marginBottom:'0.5rem'}}>
                  <h4 style={{margin:0, fontSize:'1rem', fontWeight:700, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}} title={a.title}>{a.title}</h4>
                  <button onClick={()=>onDelete(a.id)} title="Delete Deployment" style={{background:'none', border:'none', color:'var(--lywo-danger)', cursor:'pointer'}}><Icons.Trash/></button>
                </div>
                <div style={{fontSize:'0.85rem', marginBottom:'1rem'}}>
                  <span className="badge" style={{background:'#eee', color:'#333', marginRight:'0.5rem'}} title={`Assigned to ${a.groupCode}`}>Company: {a.groupCode}</span>
                  <span className="badge" style={{background:'#eee', color:'#333'}} title={`Access Code: ${a.accessCode}`}>Code: {a.accessCode}</span>
                  <div style={{marginTop:'0.5rem', color:'#666'}} title="Total Submissions">{submissions.filter(s=>s.assessmentId===a.id).length} Candidates</div>
                </div>
                <div style={{display:'flex', gap:'0.5rem', marginTop:'auto'}}>
                  <button className="btn btn-outline" style={{flex:1, padding:'0.5rem'}} onClick={()=>onAction(a, 'check')} title="Check Assessment Preview"><Icons.Play/> Check</button>
                  <button className="btn btn-primary" style={{flex:1, padding:'0.5rem'}} onClick={()=>onAction(a, 'leaderboard')} title="View Leaderboard"><Icons.Chart/> Leaderboard</button>
                  <button className="btn btn-outline" style={{flex:1, padding:'0.5rem'}} onClick={()=>onAction(a, 'adjust')} title="Adjust Weightage"><Icons.Edit/> Adjust</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <table style={{width:'100%', borderCollapse:'collapse', fontSize:'0.9rem', tableLayout: 'fixed'}}>
            <thead>
                <tr style={{borderBottom:'2px solid var(--lywo-border)', textAlign:'left'}}>
                    <th style={{padding:'0.75rem'}}>Title</th>
                    <th style={{padding:'0.75rem', width: '130px'}}>Origin</th>
                    <th style={{padding:'0.75rem', width: '150px'}}>Company</th>
                    <th style={{padding:'0.75rem', width: '100px'}}>Code</th>
                    <th style={{padding:'0.75rem', width: '100px'}}>Candidates</th>
                    <th style={{padding:'0.75rem', width: '120px'}}>Deployed</th>
                    <th style={{textAlign:'right', padding:'0.75rem', width: '160px'}}>Actions</th>
                </tr>
            </thead>
            <tbody>
              {active.map(a => (
                <tr key={a.id} style={{borderBottom:'1px solid var(--lywo-border)'}}>
                  <td style={{padding:'0.75rem'}}>
                      <div className="text-truncate-cell" style={{fontWeight: 600}} title={a.title}>{a.title}</div>
                  </td>
                  <td style={{padding:'0.75rem'}}><OriginBadge origin={a.origin} /></td>
                  <td style={{padding:'0.75rem', whiteSpace: 'nowrap', overflow:'hidden', textOverflow:'ellipsis'}} title={a.groupCode}>{a.groupCode}</td>
                  <td style={{padding:'0.75rem'}}><span style={{fontFamily:'monospace', background:'#eee', padding:'2px 6px', borderRadius:'4px'}} title="Access Code">{a.accessCode}</span></td>
                  <td style={{padding:'0.75rem'}} title="Total Submissions">{submissions.filter(s=>s.assessmentId===a.id).length}</td>
                  <td style={{padding:'0.75rem'}}><small>{new Date(a.createdAt).toLocaleDateString()}</small></td>
                  <td style={{textAlign:'right', padding:'0.75rem', whiteSpace: 'nowrap'}}>
                     <button className="btn btn-outline" style={{padding:'0.2rem 0.4rem', marginRight:'4px'}} onClick={()=>onAction(a, 'check')} title="Check Assessment Preview"><Icons.Play/></button>
                     <button className="btn btn-outline" style={{padding:'0.2rem 0.4rem', marginRight:'4px'}} onClick={()=>onAction(a, 'leaderboard')} title="View Leaderboard"><Icons.Chart/></button>
                     <button className="btn btn-outline" style={{padding:'0.2rem 0.4rem', marginRight:'4px'}} onClick={()=>onAction(a, 'adjust')} title="Adjust Weightage"><Icons.Edit/></button>
                     <button className="btn btn-danger-ghost" style={{padding:'0.2rem 0.4rem'}} onClick={()=>onDelete(a.id)} title="Delete Deployment"><Icons.Trash/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};


// --- Editor (Create/Edit) ---
const EditorView = ({ assessment, onSave, onCancel }) => {
  const [local, setLocal] = useState(JSON.parse(JSON.stringify(assessment)));
  const [editQ, setEditQ] = useState(null); // Question being edited in modal
  const [editIdx, setEditIdx] = useState(-1); // Index of question being edited


  const updateQ = (idx, field, val) => {
    const qs = [...local.questions]; qs[idx][field] = val;
    setLocal({...local, questions: qs});
  };


  const addQuestion = () => {
    const newId = crypto.randomUUID();
    const optId = crypto.randomUUID();
    const newQ = {
        id: newId,
        text: 'New Question',
        type: 'MCQ',
        weight: 1.0,
        options: [
            {id: optId, text: 'Option A'},
            {id: crypto.randomUUID(), text: 'Option B'},
            {id: crypto.randomUUID(), text: 'Option C'},
            {id: crypto.randomUUID(), text: 'Option D'}
        ],
        correctOptionId: optId
    };
    setLocal(prev => ({...prev, questions: [...prev.questions, newQ]}));
    setEditQ(newQ);
    setEditIdx(local.questions.length);
  };


  const saveModalChanges = () => {
      if (editIdx > -1 && editQ) {
          const qs = [...local.questions];
          qs[editIdx] = editQ;
          setLocal({...local, questions: qs});
      }
      setEditQ(null);
      setEditIdx(-1);
  };


  return (
    <div className="panel">
      <div className="panel-header">
        <div>
           <h3 style={{margin:0, fontSize:'1.1rem'}}>{local.status === 'In Use' ? 'Adjust Weightage' : 'Edit Quiz'}</h3>
           {local.status === 'In Use' && <small style={{color:'var(--lywo-warning)'}}>Live Mode: Edits trigger regrading</small>}
        </div>
      </div>
      <div className="panel-body" style={{background:'#f8f9fa'}}>
        <div className="card-item" style={{marginBottom:'1rem', height:'auto'}}>
           <div style={{display:'flex', gap:'1rem'}}>
              <div style={{flex:2}}>
                 <label style={{fontSize:'0.75rem', fontWeight:700, color:'#666', textTransform:'uppercase'}}>Title</label>
                 <input className="form-control" value={local.title} onChange={e=>setLocal({...local, title:e.target.value})} title="Assessment Title" />
              </div>
              <div style={{flex:1}}>
                 <label style={{fontSize:'0.75rem', fontWeight:700, color:'#666', textTransform:'uppercase'}}>Duration (Minutes)</label>
                 <input type="number" className="form-control" value={local.durationMinutes} onChange={e=>setLocal({...local, durationMinutes:parseInt(e.target.value)||0})} title="Duration in Minutes" />
              </div>
              <div style={{flex:1}}>
                 <label style={{fontSize:'0.75rem', fontWeight:700, color:'#666', textTransform:'uppercase'}}>Total Weightage</label>
                 <div style={{padding:'0.6rem', fontWeight:700, color:'var(--lywo-primary)'}} title="Sum of all question weights">{local.questions.reduce((a,b)=>a+b.weight,0).toFixed(1)}</div>
              </div>
           </div>
        </div>


        {local.questions.map((q, idx) => (
           <div key={q.id} className="card-item" style={{marginBottom:'0.5rem', height:'auto'}}>
              <div style={{display:'flex', justifyContent:'space-between', marginBottom:'0.5rem'}}>
                 <div style={{display:'flex', alignItems:'center', gap:'0.5rem'}}>
                    <span className="badge" style={{background:'#eee', color:'#555'}}>Question {idx+1}</span>
                    <div style={{display:'flex', alignItems:'center', border:'1px solid #ddd', borderRadius:'4px', padding:'0 0.25rem'}}>
                       <span style={{fontSize:'0.7rem', color:'#888', paddingRight:'4px'}}>Weightage:</span>
                       <input type="number" step="0.5" style={{border:'none', width:'50px', fontWeight:700, textAlign:'center'}} value={q.weight} onChange={e=>updateQ(idx,'weight',parseFloat(e.target.value))} title="Edit Question Weightage"/>
                    </div>
                 </div>
                 <div style={{display:'flex', gap:'0.5rem'}}>
                    <button className="btn-icon" onClick={()=>{setEditQ(JSON.parse(JSON.stringify(q))); setEditIdx(idx);}} title="Edit Question Content"><Icons.Edit/></button>
                    <button className="btn-icon" onClick={()=>{const n=[...local.questions]; n.splice(idx,1); setLocal({...local, questions:n})}} title="Delete Question"><Icons.Trash style={{color:'red'}}/></button>
                 </div>
              </div>
              <p style={{margin:'0 0 0.5rem 0', fontWeight:500}}>{q.text}</p>
             
              <div style={{display:'flex', flexDirection:'column', gap:'4px'}}>
                 {q.options.map((o,i) => {
                     const isCorrect = o.id === q.correctOptionId;
                     return (
                        <div key={i} style={{fontSize:'0.85rem', padding:'6px 12px', borderRadius:'4px', border:`1px solid ${isCorrect ? 'var(--lywo-success)' : '#eee'}`, background: isCorrect ? 'var(--lywo-success-bg)' : '#fff', display:'flex', alignItems:'center', gap:'8px', color: isCorrect ? 'var(--lywo-success)' : 'inherit'}}>
                           {isCorrect ? <Icons.Check style={{color: 'var(--lywo-success)'}} /> : <span style={{width:'1em'}}></span>}
                           <span style={{fontWeight:700, color:'#666'}}>{String.fromCharCode(65+i)}.</span> {o.text}
                        </div>
                     );
                 })}
              </div>


              {q.rationale && (
                 <div className="rationale-box">
                    <strong>Rationale:</strong> {q.rationale}
                 </div>
              )}
           </div>
        ))}
       
        <button
            className="btn btn-outline"
            style={{width:'100%', borderStyle:'dashed', borderWidth:'2px', padding:'1rem', justifyContent:'center'}}
            onClick={addQuestion}
            title="Add a new question to this quiz"
        >
            + Add Question
        </button>
      </div>
     
      <div className="sticky-action-bar">
         <button className="btn btn-outline" onClick={onCancel} title="Discard changes and go back">Cancel</button>
         <button className="btn btn-primary" onClick={()=>onSave(local)} title="Save changes and close editor">Save & Close</button>
      </div>


      {editQ && (
         <Modal title="Edit Question" onClose={()=>{setEditQ(null); setEditIdx(-1);}} footer={<button className="btn btn-primary" onClick={saveModalChanges}>Update</button>}>
            <div className="mb-3">
                <label className="form-label small fw-bold">Question Text</label>
                <textarea className="form-control" rows={3} value={editQ.text} onChange={e=>setEditQ({...editQ, text:e.target.value})} title="Enter question text"/>
            </div>
            <div className="mb-3">
                <label className="form-label small fw-bold">Options (Select Correct)</label>
                {editQ.options.map((opt, i) => (
                    <div key={i} style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px'}}>
                        <input type="radio" name="correctOpt" checked={editQ.correctOptionId === opt.id} onChange={()=>setEditQ({...editQ, correctOptionId: opt.id})} title="Mark as correct answer" />
                        <input className="form-control" style={{padding:'0.4rem'}} value={opt.text} onChange={e=>{
                            const newOpts = [...editQ.options];
                            newOpts[i] = {...newOpts[i], text: e.target.value};
                            setEditQ({...editQ, options: newOpts});
                        }} title={`Option ${String.fromCharCode(65+i)} text`} />
                    </div>
                ))}
            </div>
            <div>
                <label className="form-label small fw-bold">Rationale</label>
                <textarea className="form-control" rows={2} value={editQ.rationale || ''} onChange={e=>setEditQ({...editQ, rationale:e.target.value})} title="Explanation for the correct answer"/>
            </div>
         </Modal>
      )}
    </div>
  );
};


// --- AI Generator ---
const AiGenerator = ({ onCancel, onSave }) => {
  const [step, setStep] = useState('input');
  const [inputs, setInputs] = useState({ title: '', count: 5, duration: 30, diff: 'Medium' });
  const [questions, setQuestions] = useState([]);


  const generate = () => {
    if(!inputs.title) return alert("Title required");
    setStep('loading');
    MockService.simulateAi('GET', {topic: inputs.title, count: inputs.count})
      .then(res => { setQuestions(res.questions); setStep('review'); });
  };


  const handleAction = (type, idx) => {
    if(type === 'delete') setQuestions(questions.filter((_, i) => i !== idx));
    if(type === 'replace') MockService.simulateAi('REPLACE').then(r => { const n = [...questions]; n[idx] = r.question; setQuestions(n); });
  };


  if(step === 'loading') return <div style={{display:'flex', alignItems:'center', justifyContent:'center', height:'100%'}}>Generating...</div>;


  if(step === 'input') return (
    <div style={{display:'flex', alignItems:'center', justifyContent:'center', height:'100%'}}>
       <div className="card-item" style={{width:'100%', maxWidth:'1000px', height:'auto', padding:'2rem'}}>
          <div style={{textAlign:'center', marginBottom:'1.5rem'}}>
             <div style={{background:'#e0e7ff', width:'48px', height:'48px', borderRadius:'50%', display:'inline-flex', alignItems:'center', justifyContent:'center', color:'var(--lywo-primary)', marginBottom:'1rem'}}><Icons.Sparkles/></div>
             <h3>AI Generator</h3>
          </div>
          <label className="form-label">Assessment Title</label>
          <input className="form-control" style={{marginBottom:'1rem'}} value={inputs.title} onChange={e=>setInputs({...inputs, title:e.target.value})} placeholder="e.g. React Basics" title="Enter main topic" />
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem', marginBottom:'1.5rem'}}>
             <div><label>Questions</label><select className="form-control" value={inputs.count} onChange={e=>setInputs({...inputs, count:e.target.value})} title="Number of questions"><option value="5">5</option><option value="10">10</option></select></div>
             <div><label>Duration</label><select className="form-control" value={inputs.duration} onChange={e=>setInputs({...inputs, duration:e.target.value})} title="Time limit"><option value="15">15m</option><option value="30">30m</option></select></div>
          </div>
         
          <div className="sticky-action-bar" style={{margin: '0 -2rem -2rem -2rem', padding: '1rem 2rem', borderTop: '1px solid var(--lywo-border)'}}>
             <button className="btn btn-outline" onClick={onCancel}>Cancel</button>
             <button className="btn btn-primary" onClick={generate}>Generate</button>
          </div>
       </div>
    </div>
  );


  return (
    <div className="panel">
       <div className="panel-header"><h3>Review Questions</h3></div>
       <div className="panel-body" style={{background:'#f8f9fa'}}>
          {questions.map((q, idx) => (
             <div key={idx} className="card-item" style={{marginBottom:'0.5rem', height:'auto'}}>
                <div style={{display:'flex', justifyContent:'space-between', marginBottom:'0.5rem'}}>
                   <span className="badge badge-primary">Question {idx+1}</span>
                   <div style={{display:'flex', gap:'8px'}}>
                      <button className="btn-icon" onClick={()=>handleAction('replace', idx)} title="Replace with new question"><Icons.Refresh/></button>
                      <button className="btn-icon" onClick={()=>handleAction('delete', idx)} title="Remove question"><Icons.Trash style={{color:'red'}}/></button>
                   </div>
                </div>
                <p style={{fontWeight:500}}>{q.text}</p>
                <div className="rationale-box"><strong>Rationale:</strong> {q.rationale}</div>
             </div>
          ))}
       </div>
       <div className="sticky-action-bar">
          <button className="btn btn-outline" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onSave({ title: inputs.title, duration: inputs.duration, questions })}>Create Quiz</button>
       </div>
    </div>
  );
};


// --- View: Student Simulator ---
const StudentSimulatorView = ({ assessment, onClose, onSubmit }) => {
  const [responses, setResponses] = useState({});


  return (
    <div className="panel">
      <div className="panel-header">
        <div>
          <h3 style={{margin:0, fontSize:'1.1rem'}}>{assessment.title}</h3>
          <small style={{color:'var(--lywo-text-muted)'}}>Mode: Candidate Preview • Company: {assessment.groupCode}</small>
        </div>
        <span className="badge badge-primary">Preview Mode</span>
      </div>
      <div className="panel-body" style={{background:'#f8f9fa'}}>
        {assessment.questions.map((q, idx) => (
          <div key={q.id} className="card-item" style={{marginBottom:'1rem', height:'auto'}}>
            <div style={{marginBottom:'0.5rem', display:'flex', justifyContent:'space-between'}}>
                <span className="badge" style={{background:'#eee', color:'#555'}}>Question {idx+1}</span>
                <span style={{fontSize:'0.8em', color:'#888'}}>Weight: {q.weight}</span>
            </div>
            <p style={{margin:'0 0 1rem 0', fontWeight:500, fontSize:'1rem'}}>{q.text}</p>
            <div style={{display:'flex', flexDirection:'column', gap:'0.5rem'}}>
              {q.options.map((opt, i) => (
                <label key={opt.id} style={{display:'flex', alignItems:'center', gap:'10px', padding:'10px', border:'1px solid var(--lywo-border)', borderRadius:'6px', background:'#fff', cursor:'pointer'}}>
                  <input
                    type="radio"
                    name={q.id}
                    checked={responses[q.id] === opt.id}
                    onChange={() => setResponses({...responses, [q.id]: opt.id})}
                    style={{accentColor:'var(--lywo-primary)'}}
                  />
                  <span style={{fontWeight:600, color:'var(--lywo-text-muted)'}}>{String.fromCharCode(65+i)}.</span>
                  <span>{opt.text}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="sticky-action-bar">
         <button className="btn btn-outline" onClick={onClose}>Close Preview</button>
         <button className="btn btn-primary" onClick={onSubmit}>Submit Assessment</button>
      </div>
    </div>
  );
};


// --- Gradebook ---
const GradebookView = ({ assessment, submissions, onBack, onAdjust }) => {
  const data = submissions.filter(s => s.assessmentId === assessment.id).map(sub => {
     let score = 0, total = 0;
     const breakdown = assessment.questions.map(q => {
        const correct = sub.responses[q.id] === q.correctOptionId;
        const pts = parseFloat(q.weight);
        if(correct) score += pts;
        total += pts;
        return { correct, pts: correct ? pts : 0, w: q.weight };
     });
     return { ...sub, breakdown, score, total };
  });


  return (
    <div className="panel">
       <div className="panel-header">
          <div><h3 style={{margin:0, fontSize:'1.1rem'}}>Leaderboard: {assessment.title}</h3><small style={{color:'#666'}}>Company: {assessment.groupCode}</small></div>
          <button className="btn btn-outline" onClick={onAdjust} title="Modify question weights"><Icons.Edit/> Adjust Weightage</button>
       </div>
       <div className="panel-body">
          <table style={{width:'100%', borderCollapse:'collapse', tableLayout: 'fixed'}}>
             <thead>
                <tr style={{borderBottom:'2px solid #eee', textAlign:'left'}}>
                    <th style={{padding:'8px', width: '20%'}}>Name</th>
                    <th style={{width: '20%'}}>Date</th>
                    {assessment.questions.map((q,i) => (
                        <th key={i} style={{textAlign:'center', fontSize:'0.8em', width: '80px', whiteSpace: 'nowrap'}}>
                            Question {i+1}<br/>Weightage: {q.weight}
                        </th>
                    ))}
                    <th style={{textAlign:'right', width: '100px'}}>Score</th>
                </tr>
             </thead>
             <tbody>
                {data.map((r,i) => (
                   <tr key={i} style={{borderBottom:'1px solid #eee'}}>
                      <td style={{padding:'8px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}} title={r.studentName}>{r.studentName}</td>
                      <td>{new Date(r.submittedAt).toLocaleDateString()}</td>
                      {r.breakdown.map((b, j) => (
                          <td key={j} style={{textAlign:'center'}}>
                              <span className={`badge ${b.correct ? 'badge-success' : 'badge-warning'}`} title={`Score: ${b.pts}`}>{b.pts}</span>
                          </td>
                      ))}
                      <td style={{textAlign:'right', fontWeight:700, color:'var(--lywo-primary)'}}>{r.score}/{r.total}</td>
                   </tr>
                ))}
             </tbody>
          </table>
       </div>
       <div className="sticky-action-bar"><button className="btn btn-outline" onClick={onBack}>Back</button></div>
    </div>
  );
};


/* ==============================================================================================
   5. MAIN APP CONTROLLER
   ============================================================================================== */


export default function AssessmentNew() {
  const [view, setView] = useState('dashboard');
  const [activeItem, setActiveItem] = useState(null);
  const [repoType, setRepoType] = useState('lywo');
  const [toast, setToast] = useState(null);
 
  // Data State
  const [assessments, setAssessments] = useState([]);
  const [submissions, setSubmissions] = useState([]);


  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = LYWO_CSS;
    document.head.appendChild(style);
   
    // Initial Seed Data with Diverse Samples
    const q1 = {
        id:'q1', text:'Which pattern best completes the sequence?', type:'MCQ', weight:1.0,
        rationale:'Pattern recognition is key for problem solving.',
        options:[{id:'o1',text:'Pattern A'},{id:'o2',text:'Pattern B'},{id:'o3',text:'Pattern C'},{id:'o4',text:'Pattern D'}],
        correctOptionId:'o1'
    };
    const q2 = {
        id:'q2', text:'Select the most appropriate response.', type:'MCQ', weight:1.5,
        rationale:'Communication skills assessment.',
        options:[{id:'o5',text:'Polite'},{id:'o6',text:'Direct'},{id:'o7',text:'Casual'},{id:'o8',text:'Technical'}],
        correctOptionId:'o5'
    };
   
    // Master Assessments (Drafts/Ready)
    const m1 = { id:'m1', title:'General Aptitude Test - Level 1', origin:'Manual', status:'Master', repository:'lywo', durationMinutes:45, questions:[q1, q2], updatedAt:new Date('2023-12-05T10:00:00'), updatedBy:'HR Admin' };
    const m2 = { id:'m2', title:'Senior Frontend Developer Assessment', origin:'AI-Generated', status:'Master', repository:'lywo', durationMinutes:60, questions:[q1, q2, q1], updatedAt:new Date('2023-11-10T14:00:00'), updatedBy:'AI Bot' };
    const d1 = { id:'d_new', title:'Product Manager Case Study', origin:'Manual', status:'Draft', repository:'company', durationMinutes:90, questions:[q1], updatedAt:new Date('2023-12-01T09:30:00'), updatedBy:'Sarah H.' };
    const m3 = { id:'m3', title:'Workplace Safety & Compliance', origin:'Bulk Upload', status:'Master', repository:'company', durationMinutes:30, questions:[q2, q2, q1], updatedAt:new Date('2023-12-20T16:45:00'), updatedBy:'Compliance Team' };


    // Deployed Assessments (In Use)
    const dep1 = { ...m1, id:'dep1', title:'Eng. Batch 2024 - Aptitude', status:'In Use', groupCode:'Tesla-Engineering', accessCode:'APT-001', createdAt:new Date('2023-12-01') };
    const dep2 = { ...m2, id:'dep2', title:'Frontend Team - Q1 Skills Check', status:'In Use', groupCode:'Tesla-Frontend', accessCode:'RCT-002', createdAt:new Date('2023-12-10') };
    const dep3 = { ...m3, id:'dep3', title:'Factory Staff - Safety Cert', status:'In Use', groupCode:'Tesla-Factory', accessCode:'SFT-101', createdAt:new Date('2023-12-15') };
    const dep4 = { ...d1, id:'dep4', title:'Leadership Evaluation - Managers', status:'In Use', groupCode:'Tesla-Exec', accessCode:'MGR-500', createdAt:new Date('2023-12-28') };
   
    setAssessments([m1, m2, d1, m3, dep1, dep2, dep3, dep4]);
    setSubmissions([
        { studentId:'s1', assessmentId:'dep1', studentName:'John Doe', submittedAt:new Date(), responses:{'q1':'o1','q2':'o5'} },
        { studentId:'s2', assessmentId:'dep2', studentName:'Jane Smith', submittedAt:new Date(), responses:{'q1':'o1','q2':'o5'} }
    ]);
  }, []);


  const notify = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2500); };


  const handleEdit = (a) => { setActiveItem(a); setView('editor'); };
  const handleSave = (updated) => {
     setAssessments(prev => prev.map(a => a.id === updated.id ? {...updated, updatedAt: new Date()} : a));
     notify(updated.status === 'In Use' ? 'Recalculated' : 'Saved');
     setView(updated.status === 'In Use' ? 'gradebook' : 'dashboard');
  };
  const handleDelete = (id) => {
     if(window.confirm('Delete?')) { setAssessments(prev => prev.filter(a => a.id !== id)); notify('Deleted'); }
  };
  const handleNew = (type) => {
     if(type === 'manual') {
        const newItem = { id: crypto.randomUUID(), title: 'Untitled', origin: 'Manual', status: 'Draft', repository: repoType, durationMinutes: 30, questions: [], updatedAt: new Date(), updatedBy:'User' };
        setAssessments(prev => [newItem, ...prev]); setActiveItem(newItem); setView('editor');
     } else if (type === 'ai') {
        setView('ai');
     }
  };


  return (
    <div className="app-container">
      <header className="app-header">
        
         <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
            <button className="btn btn-primary" onClick={() => setView('create')}>+ New Quiz</button>
            <div style={{width:1, height:20, background:'#ddd'}}></div>
            <button className={`btn ${view==='dashboard'?'btn-primary':'btn-outline'}`} onClick={()=>setView('dashboard')}>Draft Quiz</button>
            <button className={`btn ${view==='active'?'btn-primary':'btn-outline'}`} onClick={()=>setView('active')}>Deployed Quiz</button>
         </div>
      </header>


      <div className="app-content">
         {view === 'dashboard' && <DashboardView assessments={assessments} repoType={repoType} setRepoType={setRepoType} companyName="Tesla Inc." onEdit={handleEdit} onDelete={handleDelete} />}
         
         {view === 'active' && <DeployedView assessments={assessments} submissions={submissions} onDelete={handleDelete}
            onAction={(a, type) => { setActiveItem(a); setView(type==='check'?'student':type==='adjust'?'editor':'gradebook'); }} />}
         
         {view === 'editor' && <EditorView assessment={activeItem} onSave={handleSave} onCancel={()=>setView(activeItem.status==='In Use'?'active':'dashboard')} />}
         
         {view === 'gradebook' && <GradebookView assessment={activeItem} submissions={submissions} onBack={()=>setView('active')} onAdjust={()=>setView('editor')} />}
         
         {view === 'ai' && <AiGenerator onCancel={()=>setView('create')} onSave={(data) => {
             const newItem = { id: crypto.randomUUID(), title: data.title, origin: 'AI-Generated', status: 'Draft', repository: repoType, durationMinutes: data.duration, questions: data.questions, updatedAt: new Date(), updatedBy:'AI' };
             setAssessments(prev => [newItem, ...prev]); setActiveItem(newItem); setView('editor'); notify('Quiz Generated');
         }} />}


         {view === 'create' && (
            <div style={{display:'flex', alignItems:'center', justifyContent:'center', height:'100%'}}>
               <div className="panel" style={{width:'100%', maxWidth:'1000px', height:'auto', padding:'2rem'}}>
                  <h3 style={{textAlign:'center', marginBottom:'2rem'}}>Create New Assessment</h3>
                  <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'1rem'}}>
                     <div className="card-item selectable-card" onClick={()=>handleNew('manual')} title="Start with a blank template" style={{cursor:'pointer', textAlign:'center', border:'2px solid var(--lywo-border)'}}>
                        <div style={{fontSize:'3rem', color:'var(--lywo-primary)', marginBottom:'1rem'}}><Icons.Edit/></div>
                        <h4 style={{marginBottom:'0.5rem'}}>Manual Creation</h4>
                        <p style={{color:'var(--lywo-text-muted)', margin:0}}>Build a quiz from scratch question by question.</p>
                     </div>
                     <div className="card-item selectable-card" onClick={()=>handleNew('ai')} title="Generate quiz using AI" style={{cursor:'pointer', textAlign:'center', border:'2px solid var(--lywo-border)'}}>
                        <div style={{fontSize:'3rem', color:'#8b5cf6', marginBottom:'1rem'}}><Icons.Sparkles/></div>
                        <h4 style={{marginBottom:'0.5rem'}}>AI Generator</h4>
                        <p style={{color:'var(--lywo-text-muted)', margin:0}}>Instantly generate a quiz from a topic or job description.</p>
                     </div>
                     <div className="card-item selectable-card" onClick={()=>{notify('Template Downloaded'); setTimeout(()=>handleNew('manual'), 500)}} title="Upload CSV or Excel" style={{cursor:'pointer', textAlign:'center', border:'2px solid var(--lywo-border)'}}>
                        <div style={{fontSize:'3rem', color:'#198754', marginBottom:'1rem'}}><Icons.Upload/></div>
                        <h4 style={{marginBottom:'0.5rem'}}>Bulk Upload</h4>
                        <p style={{color:'var(--lywo-text-muted)', margin:0}}>Import questions from an Excel spreadsheet.</p>
                     </div>
                  </div>
                 
                  {/* Sticky Action Bar for Create View - Cancel on Left */}
                  <div className="sticky-action-bar" style={{margin:'2rem -2rem -2rem -2rem', borderTop:'1px solid var(--lywo-border)', justifyContent:'flex-start'}}>
                     <button className="btn btn-outline" onClick={()=>setView('dashboard')} title="Cancel creation">Cancel</button>
                  </div>
               </div>
            </div>
         )}
         
         {/* Student Simulator: Close on Left (Back) */}
         {view === 'student' && activeItem && (
            <StudentSimulatorView
                assessment={activeItem}
                onClose={() => setView('active')}
                onSubmit={() => { setView('active'); notify('Submission Received'); }}
            />
         )}
      </div>
     
      {toast && <Toast msg={toast} />}
    </div>
  );
}

